import { RequiredOptions, OptionalOptions, isDensityType, isFilterType } from "./../types/types";

/**
 * Fetch all possible options from HTML page window, and update options objects
 */
function setUpOptions(requiredOptions: RequiredOptions, optionalOptions: OptionalOptions) {
  // Required option
  if (typeof window.tableUrl === 'string' && window.tableUrl.length > 0) {
    requiredOptions._tableUrl = window.tableUrl;
  } else {
    throw new Error('Please provide a URL (variable name: tableUrl)');
  }
  
  // Optional options
  if (Array.isArray(window.columnsToFetch) && window.columnsToFetch.every(item => typeof item === 'number')) {
    optionalOptions._columnsToFetch = window.columnsToFetch.map(entry => entry - 1); // 1-indexed from window, 0-indexed in code
  }

  if (Array.isArray(window.initialVisibleColumns) && window.initialVisibleColumns.every(item => typeof item === 'number')) {
    optionalOptions._initialVisibleColumns = window.initialVisibleColumns.map(entry => entry - 1); // 1-indexed from window, 0-indexed in code
  }

  if (typeof window.initialRowsPerPage === 'number') {
    optionalOptions._initialRowsPerPage = window.initialRowsPerPage;
  }

  if (Array.isArray(window.rowsPerPageOptions) && window.rowsPerPageOptions.every(item => typeof item === 'number')) {
    optionalOptions._rowsPerPageOptions = window.rowsPerPageOptions;
  }

  if (typeof window.initialDensity === 'string') {
    const option = window.initialDensity.toLowerCase().trim();
    if (isDensityType(option)) {
      optionalOptions._initialDensity = option;
    } else {
      console.warn('Invalid initial density. Defaulting to "comfortable".');
    }
  }

  if (typeof window.defaultFilterVariant === 'string') {
    const option = window.defaultFilterVariant.toLowerCase().trim();
    if(isFilterType(option)) {
      optionalOptions._defaultFilterVariant = option;
    } else {
      console.warn('Invalid initial density. Defaulting to "text".')
    }
  }

  if (typeof window.overrideFilterVariants === 'object' && window.overrideFilterVariants !== null ) { // Naive, just loads in and expects correct format
    optionalOptions._overrideFilterVariants = Object.fromEntries(
      Object.entries(window.overrideFilterVariants).map(([key, value]) => [
        Number(key) - 1,
        value,
      ]),
    ); // 1-indexed from window, 0-indexed in code
  }

  if (typeof window.enableSearch === 'boolean') {
    optionalOptions._enableSearch = window.enableSearch;
  }

  if (typeof window.enableFiltering === 'boolean') {
    optionalOptions._enableFiltering = window.enableFiltering;
  }

  if (typeof window.enableSorting === 'boolean') {
    optionalOptions._enableSorting = window.enableSorting;
  }

  if (typeof window.enablePagination === 'boolean') {
    optionalOptions._enablePagination = window.enablePagination;
  }
  
  if (typeof window.enableDensityToggle === 'boolean') {
    optionalOptions._enableDensityToggle = window.enableDensityToggle;
  }

  if (typeof window.enableFullScreenToggle === 'boolean') {
    optionalOptions._enableFullScreenToggle = window.enableFullScreenToggle;
  }
  
  if (typeof window.enableColumnVisibilityToggle === 'boolean') {
    optionalOptions._enableColumnVisibilityToggle = window.enableColumnVisibilityToggle;
  }

  if (typeof window.enableStickyHeader === 'boolean') {
    optionalOptions._enableStickyHeader = window.enableStickyHeader;
  }

  if (typeof window.enableModalWindow === 'boolean') {
    optionalOptions._enableModalWindow = window.enableModalWindow;
  }

  if (typeof window.maxTableHeight === 'string' && window.maxTableHeight.length > 0) {
    optionalOptions._maxTableHeight = window.maxTableHeight;
  }
}

export function getTableOptions(): { requiredOptions: RequiredOptions; optionalOptions: OptionalOptions;} {
  // Needed from HTML
  const requiredOptions: RequiredOptions = {
    _tableUrl: '',
  };

  // Optional from HTML - default values set
  const optionalOptions: OptionalOptions = {
    _columnsToFetch: [],
    _initialVisibleColumns: [],
    _initialRowsPerPage: 10,
    _rowsPerPageOptions: [5, 10, 20, 40],
    _initialDensity: 'comfortable',
    _defaultFilterVariant: 'text',
    _overrideFilterVariants: null,
    _enableSearch: true,
    _enableFiltering: true,
    _enableSorting: true,
    _enablePagination: true,
    _enableDensityToggle: true,
    _enableFullScreenToggle: true,
    _enableColumnVisibilityToggle: true,
    _enableStickyHeader: true,
    _enableModalWindow: true,
    _maxTableHeight: null
  };

  setUpOptions(requiredOptions, optionalOptions);

  return {
    requiredOptions,
    optionalOptions,
  };
}
